import {
    IonButton,
    IonCard,
    IonContent,
    IonFooter,
    IonIcon,
    IonItem,
    IonList,
    IonPage,
    IonText,
    useIonRouter,
} from "@ionic/react";
import { useTranslation } from "react-i18next";
import React, { FC } from "react";
import "../login/login.css";
import "./registerStepThree.css";
import { routes } from "../../route/constants";
import { useSelector } from "react-redux";
import { RootState } from "../../redux/store";
import { HeaderComponent } from "../../components";
import { mpUpdatePersonOnce } from "../../services/analytics/mixpanelService";
import { OnBoardingHeadingComponent } from "../onBoarding/onBoardingHeadingComponent";
import { trackEvent } from "../../services/analytics/analyticsService";
import { chevronForwardOutline } from "ionicons/icons";
import { isFeatureAvailableCurrentLanguage } from "../../utils/FeatureToggle";

const referalSources = ["friends_colleague", "social", "youtube", "search"];
const countrySpecificReferralSources: {
    [key: string]: string[];
} = {
    US: ["state_association", "nisoa", "assignr", "ref_insight"],
    GB: ["national_association", "county_association"],
    DK: ["dbu"],
    NL: ["knvb"],
};
const directTranslationKeys: {
    [key: string]: string;
} = {
    nisoa: "NISOA",
    assignr: "Assignr",
    ref_insight: "Ref Insight",
    dbu: "DBU",
    knvb: "KNVB",
};
const getAllReferralSources = (country: string) => {
    const sources = referalSources
        .concat(countrySpecificReferralSources[country] || [])
        .concat("other");

    return sources;
};
const translationBase = "help.OnboardingStepThree.sources";
const getReferalSourceTranslation = (key: string) => {
    if (directTranslationKeys[key]) {
        return directTranslationKeys[key];
    }
    return `${translationBase}.${key}`;
};
type ReferralSource = {
    key: string;
    metaData?: {
        [key: string]: string;
    };
};
const RegisterStepThree: FC = () => {
    const { t } = useTranslation();
    const profile = useSelector((root: RootState) => root.currentUser?.profile);
    const route = useIonRouter();
    const [selectedSource, setSelectedSource] = React.useState<ReferralSource>({
        key: "skip",
    });

    const NEXT_PAGE = routes.refsixOnboardingGettingStarted;

    console.log(
        'isFeatureAvailableCurrentLanguage("registrationReferrer")',
        isFeatureAvailableCurrentLanguage("registrationReferrer")
    );

    // Check if the feature is available in the current language
    if (!isFeatureAvailableCurrentLanguage("registrationReferrer")) {
        route.push(NEXT_PAGE, "forward", "replace");
        return null;
    }

    const nextPage = (source: ReferralSource) => {
        track(source);
        route.push(NEXT_PAGE, "forward", "replace");
    };
    const track = (source: ReferralSource) => {
        trackEvent("RegistrationReferal", {
            referer: source.key,
            ...source.metaData,
        });
        const properties: {
            [key: string]: any;
        } = {
            registrationReferer: source.key,
        };
        if (source.metaData) {
            Object.keys(source.metaData).forEach((key: string) => {
                properties["registrationReferer_" + key] =
                    source.metaData![key];
            });
        }
        mpUpdatePersonOnce(properties);
    };
    const handleSubmit = () => {
        nextPage(selectedSource);
    };

    const allSources = getAllReferralSources(profile?.country || "");
    const onSelectionChange = (source: ReferralSource) => {
        setSelectedSource(source);
        if (source.key === "other") {
            return;
        }
        nextPage(source);
    };

    return (
        <form onSubmit={handleSubmit}>
            <IonPage>
                <HeaderComponent
                    title={t("help.OnboardingStepThree.heading")}
                />

                <IonContent>
                    <OnBoardingHeadingComponent
                        progressValue={0.1}
                        upperTitleInBold={t("help.OnboardingStepThree.title")}
                    />

                    <IonCard className="question-container">
                        <QuestionList
                            questions={allSources}
                            selectedSource={selectedSource}
                            setSelectedSource={onSelectionChange}
                        />
                    </IonCard>
                </IonContent>
                <IonFooter>
                    <IonButton
                        expand="block"
                        onClick={handleSubmit}
                        data-testid="stepThreeButton"
                    >
                        {t("general.next")}
                    </IonButton>
                </IonFooter>
            </IonPage>
        </form>
    );
};
const QuestionList: FC<{
    questions: string[];
    selectedSource: ReferralSource;
    setSelectedSource: (source: ReferralSource) => void;
}> = ({ questions, selectedSource, setSelectedSource }) => {
    const { t } = useTranslation();
    const textAreaRef = React.useRef<HTMLTextAreaElement>(null);
    const internalOnClick = (key: string) => {
        return () => {
            // Set the focus to the textarea if the key is "other"
            if (key === "other") {
                if (textAreaRef.current) {
                    textAreaRef.current.focus();
                }
            }
            setSelectedSource({ key });
        };
    };
    const onChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const value = e.target.value;
        setSelectedSource({
            key: "other",
            metaData: {
                text: value,
            },
        });
    };
    return (
        <IonList className={"question-list"}>
            {questions.map((key, idx) => (
                <>
                    <IonItem
                        className="question-item-container"
                        key={idx}
                        onClick={internalOnClick(key)}
                    >
                        <IonText className={`question-item`}>
                            {t(getReferalSourceTranslation(key))}
                        </IonText>
                        <IonIcon
                            icon={chevronForwardOutline}
                            slot={"end"}
                            style={{ color: "var(--ion-color-primary)" }}
                        />
                    </IonItem>
                    {key === "other" && (
                        <IonItem>
                            <textarea
                                ref={textAreaRef}
                                placeholder={t(
                                    "help.OnboardingStepThree.other.placeholder"
                                )}
                                onChange={onChange}
                            ></textarea>
                        </IonItem>
                    )}
                </>
            ))}
        </IonList>
    );
};
export default RegisterStepThree;
